import { useEffect, useState } from "react";
import Plot from 'react-plotly.js';
import axios from '../axiosConfig';
import { Container as MapDiv, NaverMap, Marker, useNavermaps } from 'react-naver-maps'
import SingleBoreholeMarker from "./SingleBoreholeMarker";
import SoilTypeLegend from "./SoilTypeLegend";
import downloadExcel from "../utils/downloadExcel";
import { S, calculateFa, calculateFv } from "../utils/sptCalculations";

function BoreholeDetailView({ selectedBorehole, showToast, boreholeUpdateDelegate }) {
  const navermaps = useNavermaps()
  const [map, setMap] = useState(null)
  const [borehole, setBorehole] = useState(selectedBorehole);
  const [boreholeX, setBoreholeX] = useState(selectedBorehole.x);
  const [boreholeY, setBoreholeY] = useState(selectedBorehole.y);
  const [coordinateSystem, setCoordinateSystem] = useState(selectedBorehole.coordinate_system);
  const [epsgCode, setEpsgCode] = useState(selectedBorehole.epsg_code);
  const [isEditingCoord, setIsEditingCoord] = useState(false);

  // 좌표 변경 핸들러
  const handleXChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setBoreholeX(value);
    }
  };

  const handleYChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setBoreholeY(value);
    }
  };

  const swapCoordinates = () => {
    const tempX = boreholeX;
    setBoreholeX(boreholeY);
    setBoreholeY(tempX);
    showToast("좌표 정보 업데이트를 완료해야 변경 사항이 반영됩니다.");
  };

  const handleUpdateCoordinate = () => {
    axios.patch(`/boreholes/${borehole.id}?x=${boreholeX}&y=${boreholeY}&coordinate_system=${coordinateSystem}&epsg_code=${epsgCode}`)
      .then((response) => {
        showToast("좌표 정보가 성공적으로 갱신되었습니다.");
        setBorehole(response.data.data);
        boreholeUpdateDelegate(response.data.data);
        setIsEditingCoord(false);
      })
      .catch((error) => {
        console.error("좌표 저장 중 오류 발생:", error);
        showToast("좌표 저장에 실패했습니다.", 'error');
      });
  };

  const renderStackedSPTGraphWithLine = () => {
    const sortedSpts = borehole.spts.sort((a, b) => b.elevation - a.elevation);
    const filteredSptsForBar = sortedSpts.filter(spt => spt.elevation !== null && spt.n === null && spt.d === null);
    const filteredSptsForLine = sortedSpts.filter(spt => spt.elevation !== null && spt.n !== null && spt.d !== null);

    if (filteredSptsForBar.length === 0 && filteredSptsForLine.length === 0) {
      return <p>해당 데이터가 없습니다.</p>;
    }

    filteredSptsForBar.sort((a, b) => b.elevation - a.elevation);
    const minElevation = Math.min(...sortedSpts.map(spt => spt.elevation));

    const highValues = filteredSptsForBar.map(spt => spt.elevation);
    const lowValues = highValues.map((elev, index) => {
      return index === filteredSptsForBar.length - 1 ? minElevation : highValues[index + 1];
    });

    const spectralColors = [
      '#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b',
      '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'
    ];

    const barData = filteredSptsForBar.map((spt, index) => ({
      x: [0],
      y: [highValues[index] - lowValues[index]],
      base: [lowValues[index]],
      type: 'bar',
      width: 0.2,
      name: `Layer ${index + 1}`,
      marker: {
        color: spectralColors[index % spectralColors.length],
        line: {
          color: 'black',
          width: 1.5,
        },
      },
      hovertemplate: `Borehole: ${borehole.name}<br>High: ${highValues[index].toFixed(2)}<br>Low: ${lowValues[index].toFixed(2)}<extra></extra>`,
    }));

    const lineData = {
      x: filteredSptsForLine.map(() => 0),
      y: filteredSptsForLine.map(spt => spt.elevation),
      mode: 'lines+markers',
      type: 'scatter',
      name: 'SPT N값',
      marker: {
        color: 'blue',
        size: 6,
      },
      line: {
        color: 'blue',
        width: 2,
      },
      hovertemplate: `SPT-N: %{text}<br>Elevation: %{y}<extra></extra>`,
      text: filteredSptsForLine.map(spt => spt.n),
      xaxis: 'x',
    };

    const transformedLineData = {
      ...lineData,
      x: filteredSptsForLine.map(spt => (spt.n / 50) * 0.2 - 0.1),
    };

    return (
      <Plot
        data={[...barData, transformedLineData]}
        layout={{
          xaxis: {
            title: 'Boreholes',
            tickvals: [0],
            ticktext: [borehole.name],
            range: [-0.5, 0.5],
          },
          yaxis: {
            title: 'Elevation',
            showspikes: true,
            spikemode: 'across',
            spikesnap: 'cursor',
            spikethickness: 2,
            spikecolor: 'black',
          },
          barmode: 'stack',
          autosize: true,
          margin: { l: 50, r: 20, t: 20, b: 50 }
        }}
        config={{
          responsive: true,
          displayModeBar: false,
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler={true}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* 기본 정보 섹션 */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-secondary-900">기본 정보</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => downloadExcel(borehole, borehole.spts)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors shadow-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-medium">Excel 다운로드</span>
            </button>
            <div className="relative group">
              <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors shadow-medium">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium">표준서식 다운로드</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-hard opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('token');
                      axios.get(`/boreholes/${borehole.id}/export/시추공정보`, {
                        responseType: 'blob',
                        headers: { Authorization: `Bearer ${token}` }
                      })
                        .then(response => {
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `${borehole.name}_시추공정보.xls`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          showToast('시추공정보 파일이 다운로드되었습니다.');
                        })
                        .catch(error => {
                          console.error('다운로드 실패:', error);
                          showToast('파일 다운로드에 실패했습니다.');
                        });
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-secondary-50 text-secondary-900 transition-colors font-medium"
                  >
                    시추공정보
                  </button>
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('token');
                      axios.get(`/boreholes/${borehole.id}/export/지층정보`, {
                        responseType: 'blob',
                        headers: { Authorization: `Bearer ${token}` }
                      })
                        .then(response => {
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `${borehole.name}_지층정보.xls`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          showToast('지층정보 파일이 다운로드되었습니다.');
                        })
                        .catch(error => {
                          console.error('다운로드 실패:', error);
                          showToast('파일 다운로드에 실패했습니다.');
                        });
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-secondary-50 text-secondary-900 transition-colors font-medium"
                  >
                    지층정보
                  </button>
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('token');
                      axios.get(`/boreholes/${borehole.id}/export/기본현장시험정보`, {
                        responseType: 'blob',
                        headers: { Authorization: `Bearer ${token}` }
                      })
                        .then(response => {
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `${borehole.name}_기본현장시험정보.xls`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          showToast('기본현장시험정보 파일이 다운로드되었습니다.');
                        })
                        .catch(error => {
                          console.error('다운로드 실패:', error);
                          showToast('파일 다운로드에 실패했습니다.');
                        });
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-secondary-50 text-secondary-900 transition-colors font-medium"
                  >
                    기본현장시험정보
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-xs text-secondary-600 mb-1">시추공 이름</p>
            <p className="text-lg font-bold text-secondary-900">{borehole.name}</p>
          </div>
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-xs text-secondary-600 mb-1">주소</p>
            <p className="text-lg font-bold text-secondary-900">{borehole.address}</p>
          </div>
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-xs text-secondary-600 mb-1">지하수위</p>
            <p className="text-lg font-bold text-secondary-900">{borehole.ground_water_table}m</p>
          </div>
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-xs text-secondary-600 mb-1">기반암 깊이</p>
            <p className="text-lg font-bold text-secondary-900">{borehole.bedrock_depth_h}m</p>
          </div>
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-xs text-secondary-600 mb-1">기반암 표고</p>
            <p className="text-lg font-bold text-secondary-900">
              {borehole.first_vs_760_elevation ? `${borehole.first_vs_760_elevation}m` : "측정 안됨"}
            </p>
          </div>
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-xs text-secondary-600 mb-1">토층평균전단파속도</p>
            <p className="text-lg font-bold text-secondary-900">{Math.round(borehole.vs_soil, 0)}m/s</p>
          </div>
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-xs text-secondary-600 mb-1">지반 종류</p>
            <p className="text-lg font-bold text-green-600">
              {borehole.soil_type || "분류 불가"}
              {borehole.is_temp_soil_type && (
                <span className="text-xs text-secondary-500 ml-2">(임시분류)</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* 지도 섹션 */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <h2 className="text-xl font-bold text-secondary-900 mb-4">위치 정보</h2>
        <div className="relative rounded-xl overflow-hidden shadow-medium" style={{ height: '500px' }}>
          <MapDiv style={{ width: '100%', height: '100%' }}>
            <NaverMap
              center={new navermaps.LatLng(borehole.grs80_x, borehole.grs80_y)}
              defaultZoom={15}
              ref={setMap}
            >
              <div className="absolute top-4 left-4 z-10">
                <button
                  onClick={() => {
                    if (map) {
                      map.setCenter(new navermaps.LatLng(borehole.grs80_x, borehole.grs80_y))
                    }
                  }}
                  className="px-4 py-2 bg-white hover:bg-secondary-50 text-secondary-700 rounded-xl shadow-medium transition-colors font-medium text-sm"
                >
                  현재 위치로 이동
                </button>
              </div>
              <SingleBoreholeMarker map={map} borehole={borehole} />
            </NaverMap>
            <SoilTypeLegend />
          </MapDiv>
        </div>
      </div>

      {/* 좌표 정보 섹션 */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-secondary-900">좌표 정보</h2>
          {!isEditingCoord && (
            <button
              onClick={() => setIsEditingCoord(true)}
              className="px-4 py-2 text-sm bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-xl transition-colors"
            >
              수정
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-secondary-600 mb-1">타원체</p>
              <p className="text-base font-medium text-secondary-900">GRS80 (고정)</p>
            </div>
            <div>
              <p className="text-sm text-secondary-600 mb-1">투영방법</p>
              <p className="text-base font-medium text-secondary-900">UTM (고정)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-secondary-700 mb-2 block">X 좌표</label>
              <input
                type="number"
                value={boreholeX}
                onChange={handleXChange}
                disabled={!isEditingCoord}
                className="w-full px-4 py-2 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-secondary-50 disabled:text-secondary-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-secondary-700 mb-2 block">Y 좌표</label>
              <input
                type="number"
                value={boreholeY}
                onChange={handleYChange}
                disabled={!isEditingCoord}
                className="w-full px-4 py-2 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-secondary-50 disabled:text-secondary-500"
              />
            </div>
          </div>

          {isEditingCoord && (
            <>
              <button
                onClick={swapCoordinates}
                className="w-full py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl transition-colors"
              >
                X와 Y 좌표 바꾸기
              </button>

              <div>
                <label className="text-sm font-medium text-secondary-700 mb-2 block">좌표 기준</label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="new"
                      checked={coordinateSystem === 'new'}
                      onChange={() => setCoordinateSystem('new')}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-secondary-700">신좌표 (200,000mE, 600,000mN)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="old"
                      checked={coordinateSystem === 'old'}
                      onChange={() => setCoordinateSystem('old')}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-secondary-700">구좌표 (200,000mE, 500,000mN)</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-secondary-700 mb-2 block">원점 선택</label>
                <select
                  value={epsgCode}
                  onChange={(e) => setEpsgCode(e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="EPSG:5181">중부</option>
                  <option value="EPSG:5185">서부</option>
                  <option value="EPSG:5187">동부</option>
                  <option value="EPSG:5188">동해</option>
                </select>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleUpdateCoordinate}
                  className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors font-medium shadow-medium"
                >
                  좌표 정보 업데이트
                </button>
                <button
                  onClick={() => {
                    setBoreholeX(selectedBorehole.x);
                    setBoreholeY(selectedBorehole.y);
                    setCoordinateSystem(selectedBorehole.coordinate_system);
                    setEpsgCode(selectedBorehole.epsg_code);
                    setIsEditingCoord(false);
                  }}
                  className="px-6 py-3 bg-secondary-200 hover:bg-secondary-300 text-secondary-700 rounded-xl transition-colors font-medium"
                >
                  취소
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 지진 정보 섹션 */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <h2 className="text-xl font-bold text-secondary-900 mb-4">지진 정보</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-sm text-secondary-600 mb-1">주소</p>
            <p className="text-base font-medium text-secondary-900">{borehole.address}</p>
          </div>
          <div className="bg-secondary-50 rounded-xl p-4">
            <p className="text-sm text-secondary-600 mb-1">지진구역</p>
            <p className="text-base font-bold text-red-600">{borehole.region_type == 1 ? "Ⅰ" : "Ⅱ"}</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-secondary-900 mb-3">재현주기별 지반 증폭 계수</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-secondary-100">
                <th className="px-4 py-3 text-left text-sm font-semibold text-secondary-700 border-b-2 border-secondary-300">항목</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-secondary-700 border-b-2 border-secondary-300">500년</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-secondary-700 border-b-2 border-secondary-300">1000년</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-secondary-700 border-b-2 border-secondary-300">2400년</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-secondary-700 border-b-2 border-secondary-300">4800년</th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-secondary-50 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-secondary-900 border-b border-secondary-200">S</td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">{S(borehole.region_type, 500).toFixed(3)}</td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">{S(borehole.region_type, 1000).toFixed(3)}</td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">{S(borehole.region_type, 2400).toFixed(3)}</td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">{S(borehole.region_type, 4800).toFixed(3)}</td>
              </tr>
              <tr className="hover:bg-secondary-50 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-secondary-900 border-b border-secondary-200">Fa</td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">
                  {borehole.soil_type != null ? calculateFa(borehole.soil_type, S(borehole.region_type, 500)).toFixed(1) : "계산 불가"}
                </td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">
                  {borehole.soil_type != null ? calculateFa(borehole.soil_type, S(borehole.region_type, 1000)).toFixed(1) : "계산 불가"}
                </td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">
                  {borehole.soil_type != null ? calculateFa(borehole.soil_type, S(borehole.region_type, 2400)).toFixed(1) : "계산 불가"}
                </td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700 border-b border-secondary-200">
                  {borehole.soil_type != null ? calculateFa(borehole.soil_type, S(borehole.region_type, 4800)).toFixed(1) : "계산 불가"}
                </td>
              </tr>
              <tr className="hover:bg-secondary-50 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-secondary-900">Fv</td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700">
                  {borehole.soil_type != null ? calculateFv(borehole.soil_type, S(borehole.region_type, 500)).toFixed(1) : "계산 불가"}
                </td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700">
                  {borehole.soil_type != null ? calculateFv(borehole.soil_type, S(borehole.region_type, 1000)).toFixed(1) : "계산 불가"}
                </td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700">
                  {borehole.soil_type != null ? calculateFv(borehole.soil_type, S(borehole.region_type, 2400)).toFixed(1) : "계산 불가"}
                </td>
                <td className="px-4 py-3 text-sm text-center text-secondary-700">
                  {borehole.soil_type != null ? calculateFv(borehole.soil_type, S(borehole.region_type, 4800)).toFixed(1) : "계산 불가"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* SPT 데이터 - 테이블과 차트를 2컬럼으로 표시 */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <h2 className="text-xl font-bold text-secondary-900 mb-4">표준관입시험 데이터 (SPT)</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 왼쪽: SPT 데이터 테이블 */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-secondary-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-700 border-b-2 border-secondary-300">레이어</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-secondary-700 border-b-2 border-secondary-300">표고</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-secondary-700 border-b-2 border-secondary-300">SPT-N</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-secondary-700 border-b-2 border-secondary-300">SPT-D</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-secondary-700 border-b-2 border-secondary-300">액상화</th>
                </tr>
              </thead>
              <tbody>
                {borehole.spts.sort((a, b) => b.elevation - a.elevation).map((spt, index) => (
                  <tr key={spt.id} className="hover:bg-secondary-50 transition-colors">
                    <td className="px-3 py-2 text-xs text-secondary-900 border-b border-secondary-200">{spt.layer_index}</td>
                    <td className="px-3 py-2 text-xs text-center text-secondary-700 border-b border-secondary-200">{spt.elevation}</td>
                    <td className="px-3 py-2 text-xs text-center text-secondary-700 border-b border-secondary-200">{spt.n}</td>
                    <td className="px-3 py-2 text-xs text-center text-secondary-700 border-b border-secondary-200">{spt.d}</td>
                    <td className="px-3 py-2 text-center border-b border-secondary-200">
                      <span className={`inline-block px-2 py-0.5 rounded-lg text-xs font-semibold ${
                        spt.liquefaction_assessment === '안전'
                          ? 'bg-green-100 text-green-700'
                          : spt.liquefaction_assessment === '평가 필요'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-secondary-100 text-secondary-600'
                      }`}>
                        {spt.liquefaction_assessment || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 오른쪽: SPT 시각화 차트 */}
          <div className="flex flex-col" style={{ minHeight: '500px' }}>
            <h3 className="text-base font-semibold text-secondary-900 mb-2">
              {borehole.name} SPT 시각화 (N - Layer, Elevation)
            </h3>
            <div className="flex-1" style={{ minHeight: '450px' }}>
              {renderStackedSPTGraphWithLine()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BoreholeDetailView;
